import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaBullhorn, FaSearch, FaChartLine, FaEnvelope, FaMobileAlt, FaPenNib,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaRocket, FaUsers, FaGlobe
} from 'react-icons/fa';
import {
    SiGoogleads, SiMeta, SiLinkedin, SiHubspot, SiMailchimp, SiSemrush, SiGoogleanalytics, SiCanva
} from 'react-icons/si';
import heroImage from '../../assets/dd9dacf7b4541a45d814672304f87d0c.jpg';

const DigitalMarketingPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaSearch, title: 'SEO Optimization', desc: 'Dominate search rankings and drive organic traffic with data-driven SEO strategies.', image: 'https://images.unsplash.com/photo-1571786256017-aee7a0c009b6?w=600' },
        { icon: FaBullhorn, title: 'Social Media Marketing', desc: 'Build a loyal community and amplify your brand voice on Instagram, LinkedIn, and Facebook.', image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600' },
        { icon: FaChartLine, title: 'PPC Advertising', desc: 'Maximize ROI with targeted Google Ads and social media paid campaigns.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { icon: FaPenNib, title: 'Content Marketing', desc: 'Engage your audience with compelling blogs, videos, and articles that tell your story.', image: 'https://images.unsplash.com/photo-1499750310159-52f0f83ec631?w=600' },
        { icon: FaEnvelope, title: 'Email Automation', desc: 'Nurture leads and convert customers with personalized, automated email journeys.', image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=600' },
        { icon: FaMobileAlt, title: 'App Store Optimization', desc: 'Improve your app visibility and downloads on Play Store and App Store.', image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600' }
    ];

    const tools = [
        { name: 'Google Ads', icon: SiGoogleads, desc: 'PPC Campaigns' },
        { name: 'Meta Ads', icon: SiMeta, desc: 'Social Targeting' },
        { name: 'LinkedIn', icon: SiLinkedin, desc: 'B2B Marketing' },
        { name: 'HubSpot', icon: SiHubspot, desc: 'CRM & Automation' },
        { name: 'Mailchimp', icon: SiMailchimp, desc: 'Email Marketing' },
        { name: 'SEMrush', icon: SiSemrush, desc: 'SEO Analytics' },
        { name: 'Analytics', icon: SiGoogleanalytics, desc: 'Performance Tracking' },
        { name: 'Canva', icon: SiCanva, desc: 'Creative Design' }
    ];

    // Horizontal Flow Steps (Marketing Funnel)
    const flowSteps = [
        { title: 'Research', icon: FaSearch, color: 'from-blue-500 to-cyan-500' },
        { title: 'Strategy', icon: FaGlobe, color: 'from-purple-500 to-indigo-500' },
        { title: 'Content', icon: FaPenNib, color: 'from-pink-500 to-rose-500' },
        { title: 'Launch', icon: FaRocket, color: 'from-orange-400 to-red-500' },
        { title: 'Optimize', icon: FaChartLine, color: 'from-green-400 to-emerald-600' },
        { title: 'Scale', icon: FaUsers, color: 'from-yellow-400 to-amber-500' }
    ];

    const faqs = [
        { q: 'How long does it take to see results from SEO?', a: 'SEO is a long-term strategy. Significant results typically appear within 3-6 months as search engines index and rank your optimized content.' },
        { q: 'Which social media platforms should my business use?', a: 'It depends on your audience. B2B businesses thrive on LinkedIn, while lifestyle brands see better engagement on Instagram and TikTok.' },
        { q: 'What is the difference between SEO and PPC?', a: 'SEO focuses on organic traffic and long-term growth, while PPC (Pay-Per-Click) provides immediate visibility through paid ads.' },
        { q: 'Do you create the content for campaigns?', a: 'Yes! Our team includes copywriters, designers, and video editors to create high-quality content for all marketing channels.' },
        { q: 'How do you measure campaign success?', a: 'We track KPIs like conversion rate, cost per acquisition (CPA), click-through rate (CTR), and overall ROI using advanced analytics tools.' }
    ];

    const whyChooseUs = [
        { title: 'Data-Driven Approach', desc: 'We don\'t guess; we use real-time data to optimize campaigns for maximum ROI.' },
        { title: 'Omnichannel Strategy', desc: 'Seamlessly integrating SEO, Social, and Email for a unified brand experience.' },
        { title: 'Creative Excellence', desc: 'Combining analytical precision with stunning visuals and compelling copy.' },
        { title: 'Transparent Reporting', desc: 'Weekly reports showing exactly where your budget is going and the results it\'s driving.' },
        { title: 'Agile Adaptation', desc: 'Quickly pivoting strategies based on market trends and algorithm updates.' },
        { title: 'Dedicated Support', desc: 'A dedicated account manager to guide you through every step of your growth.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 via-indigo-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-purple-300 mb-3">What We Do &gt; Growth</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Digital Marketing</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Amplify your brand presence and drive measurable growth with our 360° marketing strategies.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-purple-700 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Free Audit
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-purple-700 transition-all text-sm whitespace-nowrap">
                                Explore Services
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Results That Matter</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            We focus on metrics that impact your bottom line: Leads, Sales, and ROI.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 text-center divide-x divide-slate-200">
                        {[
                            { label: 'Leads Generated', val: '50k+' },
                            { label: 'Ad Spend Managed', val: '$2M+' },
                            { label: 'Avg. ROI', val: '450%' },
                            { label: 'Client Retention', val: '95%' }
                        ].map((stat, idx) => (
                            <div key={idx} className="p-4">
                                <div className="text-4xl font-bold text-purple-600 mb-2">{stat.val}</div>
                                <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies / Tools */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Marketing Tech Stack</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Leveraging premium tools for precision targeting and analytics.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {tools.map((tool, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <tool.icon className="text-5xl mx-auto mb-4 text-purple-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{tool.name}</h3>
                                <p className="text-sm text-slate-600">{tool.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Marketing Solutions</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Tailored strategies to boost your online visibility and revenue.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4 text-purple-300" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-600 to-indigo-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Partner With Us?</h2>
                        <p className="text-lg text-purple-100 max-w-3xl mx-auto">
                            We are not just an agency; we are your growth partners.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-purple-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-purple-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Marketing Funnel with Arrows) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Our Growth Process</h2>
                        <p className="text-slate-600">From brand awareness to loyal advocacy.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-purple-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Frequently Asked Questions</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-purple-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Get Your Marketing Plan</h2>
                            <p className="text-slate-600 mb-6">Fill out the form below to skyrocket your growth.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="John Doe" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Service Interest</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600">
                                        <option>SEO Services</option>
                                        <option>Social Media Marketing</option>
                                        <option>PPC Advertising</option>
                                        <option>Content Marketing</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                                    <textarea rows="4" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="Tell us about your business goals..."></textarea>
                                </div>
                                <button type="submit" className="w-full bg-purple-600 text-white font-medium py-3 rounded-sm hover:bg-purple-700 transition-colors shadow-lg">
                                    Send Inquiry
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-purple-700 to-indigo-800 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Ready to Grow Your Brand?</h2>
                    <p className="text-xl font-light mb-8 text-purple-100">
                        Join hundreds of businesses scaling with our digital strategies.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-purple-800 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Start Growth
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-purple-800 transition-all text-sm whitespace-nowrap">
                            Call Strategist
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DigitalMarketingPage;
